import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import classNames from "classnames";

import { TaskType } from "src/types";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ROUTES, getRoute } from "src/utils/ROUTES";
import { Img } from "src/app/components/Img/Img";
import { ProjectBasicData } from "src/redux/project/types";
import { TableTaskData } from "src/app/project/SingleProject/TasksTable/types";
import { ProjectPermissionsEnum } from "src/utils/PermissionsEnums";
import { ReactComponent as ClientOrangeIcon } from "src/images/client-orange.svg";
import { ReactComponent as PlusAltIcon } from "src/images/plus-alt.svg";
import placeholderIcon from "src/images/placeholder-image.svg";
import publicationPlaceholder from "src/images/publication-placeholder-grey.svg";
import creatorPlaceholder from "src/images/creator-placeholder.svg";
import projectPlaceholder from "src/images/project-placeholder.svg";
import { ReactComponent as ClientGreyIcon } from "src/images/client-grey.svg";
import {
  tableDataType,
  taskAvatarShape as TaskAvatarShape,
} from "../../../components/Table/Table";
import { Metadata } from "../../../Task/Subtask/Subtask";
import {
  changeTaskMetaFieldValue,
  hideShareMultipleColumnsModal,
  hideStopSharingWithClientModal,
  openShareMultipleColumnsModal,
  openStopSharingWithClientModal,
} from "../../../../redux";
import { TASK_TYPES } from "../../../../utils/variables";
import Skeleton, {
  SkeletonCircle,
  SkeletonText,
} from "../../../components/Skeleton/Skeleton";
import Check from "../../../components/Check/Check";
import SocialProfileIcons from "../../../components/IconsWithStatus/SocialProfileIcons/SocialProfileIcons";
import { Button } from "../../../components/Button/Button";
import AddSocialProfilesModal from "../../../modals/AddSocialProfilesModal/AddSocialProfilesModal";
import UpdatePublicationLinkModal from "../../../modals/UpdatePublicationLinkModal/UpdatePublicationLinkModal";
import { ShareMultipleColumnsModal } from "../../../modals/ShareMultipleColumnsModal/ShareMultipleColumnsModal";
import Tooltip from "../../../components/Tooltip/Tooltip";
import StopSharingWithClientModal from "../../../modals/StopSharingWithClientModal/StopSharingWithClientModal";
import { getImageSource } from "../../../../utils/methods";
import XssFilter from "../../../components/XssFilter/XssFilter";
import TaskInfoPublicationControls from "./components/TaskInfoPublicationControls";
import { TableContext } from "../../TableField/types";

interface TaskInfoInterface {
  activeWorkspaceUuid: string;
  dataType: tableDataType;
  item: TableTaskData;
  parentLoading?: boolean;
  showShareMultipleColumnsModal: string;
  showStopSharingWithClientModal: string;
  taskAvatarShape: TaskAvatarShape;
  taskType: string;
  tasksLoading: boolean;
  projectBasicData: ProjectBasicData | null;
  tableContext: TableContext;
}

export default function TaskInfo({
  activeWorkspaceUuid,
  dataType,
  item,
  parentLoading,
  showShareMultipleColumnsModal,
  showStopSharingWithClientModal,
  taskAvatarShape,
  taskType,
  tasksLoading,
  projectBasicData,
  tableContext,
}: TaskInfoInterface) {
  const dispatch = useDispatch();

  const [coverImageUrl, setCoverImageUrl] = useState<any>(
    getImageSource(item?.cover, "tiny"),
  );
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [showAddSocialProfilesModal, setShowAddSocialProfilesModal] =
    useState(false);
  const [showUpdatePublicationLinkModal, setShowUpdatePublicationLinkModal] =
    useState<boolean>(false);

  useEffect(() => {
    const src = getImageSource(item?.cover, "tiny");

    if (item.cover === null) {
      setCoverImageUrl(null);
    } else if (src && src !== coverImageUrl) {
      setCoverImageUrl(src);
    }
  }, [item.cover]);

  useEffect(() => {
    const completed = item.metadata.find(
      (field: Metadata) => field.key === "completed",
    );

    if (completed) {
      setIsCompleted(completed.value);
    }
  }, [item]);

  const setTaskCompleted = () => {
    const completed = item.metadata.find(
      (field: Metadata) => field.key === "completed",
    );

    if (completed) {
      dispatch(
        changeTaskMetaFieldValue(item.taskId, completed.uuid, !isCompleted),
      );
    }
  };

  const isTask = () => {
    return TASK_TYPES.includes(item?.taskType);
  };

  const isProject = () => {
    return !isTask();
  };

  const openSocialMediaModal = (e: any) => {
    e.stopPropagation();
    setShowAddSocialProfilesModal(true);
  };

  const handleGettRoute = () => {
    const path =
      tableContext === "workspaceTasksList"
        ? ROUTES.WORKSPACE_TASKS_SUMMARY_DETAILS
        : ROUTES.TASK_DETAILS;

    return getRoute(
      path,
      {
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsProjectUuid: item.wsProjectUuid,
        wsTaskUuid: item.taskId,
      },
      { taskType },
    );
  };

  const openUpdatePublicationLinkModal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    // TODO: check why here is stopPropagation
    e.stopPropagation();
    setShowUpdatePublicationLinkModal(true);
  };

  const getPlaceholderImage = (cover = true) => {
    if (coverImageUrl && cover) {
      return coverImageUrl;
    }

    if (item.taskType === TaskType.Creator) {
      return creatorPlaceholder;
    }

    if (item.taskType === TaskType.Publication) {
      return publicationPlaceholder;
    }

    if (isProject()) {
      return projectPlaceholder;
    }

    if (isTask()) {
      return placeholderIcon;
    }

    return placeholderIcon;
  };

  const getCoverSkeleton = () => {
    switch (taskType) {
      case TaskType.Action:
      case TaskType.Payment:
        return <SkeletonCircle size={16} />;

      case TaskType.Publication:
      case TaskType.Content:
        return <Skeleton width={48} height={48} borderRadius={8} />;

      default:
        return <SkeletonCircle size={48} />;
    }
  };

  if (tasksLoading && dataType === tableDataType.Task) {
    return (
      <div className="tasks-table__td-info-loader">
        {getCoverSkeleton()}
        <SkeletonText width={60} height={15} marginLeft={12} />
      </div>
    );
  }

  const hasShareProjectResourcesPermission = () =>
    projectBasicData?.permissions?.project.includes(
      ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
    );

  const openShareMultipleColumnsModalWrapper = () => {
    if (hasShareProjectResourcesPermission()) {
      dispatch(openShareMultipleColumnsModal(item.taskId));
    }
  };

  const openStopSharingWithClientModalWrapper = () => {
    if (hasShareProjectResourcesPermission()) {
      dispatch(openStopSharingWithClientModal(item.taskId));
    }
  };

  return (
    <div className="ws-table__content ws-table__content--tasks-table">
      <div className="ws-table__row-info">
        {parentLoading ? (
          item.taskType !== TaskType.Creator ? (
            <Skeleton width={48} height={48} borderRadius={8} />
          ) : (
            <SkeletonCircle size={48} />
          )
        ) : item.taskType !== TaskType.Action &&
          item.taskType !== TaskType.Payment ? (
          <Link
            to={handleGettRoute()}
            className={classNames("ws-table__image", {
              "ws-table__image--square": item.taskType !== TaskType.Creator,
            })}
          >
            <Img
              className={classNames("ws-table__image-cover", {
                "ws-table__image-cover--circle":
                  taskAvatarShape === TaskAvatarShape.Circle,
                "ws-table__image-cover--square":
                  taskAvatarShape === TaskAvatarShape.Square,
              })}
              src={getPlaceholderImage()}
              fallbackImageSrc={getPlaceholderImage(false)}
              alt=""
            />
          </Link>
        ) : (
          <Check
            isCheck={isCompleted}
            setIsCheck={(value) => {
              setIsCompleted(value);
              setTaskCompleted();
            }}
          />
        )}
        {parentLoading ? (
          <Skeleton width={80} height={15} variant="text" marginLeft={12} />
        ) : (
          <div
            className={classNames("ws-table__name", {
              "ws-table__name--with-tick":
                item.taskType === TaskType.Action ||
                item.taskType === TaskType.Payment,
            })}
          >
            <div className="ws-table__task-name-wrapper">
              <Link
                key={item.title}
                to={handleGettRoute()}
                className="ws-table__task-name"
              >
                <div
                  dangerouslySetInnerHTML={{ __html: XssFilter(item.title) }}
                />
              </Link>
              {projectBasicData?.permissions?.project.includes(
                ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES_PREVIEW,
              ) &&
                projectBasicData?.clientMode && (
                  <Tooltip
                    center
                    content={
                      item.shared ? (
                        <IDHFormattedMessage
                          id="ws_tooltip_click_to_stop_sharing"
                          defaultMessage="Click to stop sharing with Client"
                        />
                      ) : (
                        <IDHFormattedMessage
                          id="ws_tooltip_click_to_share"
                          defaultMessage="Click to share with Client"
                        />
                      )
                    }
                    contentHidden={
                      !projectBasicData?.permissions?.project.includes(
                        ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
                      )
                    }
                  >
                    {item.shared ? (
                      <ClientOrangeIcon
                        className={classNames("ws-table__task-shared", {
                          "ws-table__task-shared--disabled":
                            !projectBasicData?.permissions?.project.includes(
                              ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
                            ),
                        })}
                        onClick={() => openStopSharingWithClientModalWrapper()}
                      />
                    ) : (
                      <ClientGreyIcon
                        className={classNames("ws-table__task-not-shared", {
                          "ws-table__task-not-shared--disabled":
                            !projectBasicData?.permissions?.project.includes(
                              ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
                            ),
                        })}
                        onClick={() => openShareMultipleColumnsModalWrapper()}
                      />
                    )}
                  </Tooltip>
                )}
            </div>

            {item.taskType === TaskType.Creator && (
              <div>
                {item.socialProfiles.length ? (
                  <SocialProfileIcons data={item.socialProfiles} />
                ) : (
                  <Button variant="transparent" onClick={openSocialMediaModal}>
                    <PlusAltIcon />
                    <IDHFormattedMessage
                      id="ws_add_social_profiles"
                      defaultMessage="Add social profiles"
                    />
                  </Button>
                )}
              </div>
            )}

            {item.taskType === TaskType.Publication && (
              <TaskInfoPublicationControls
                taskItem={item}
                openUpdatePublicationLinkModal={openUpdatePublicationLinkModal}
              />
            )}
          </div>
        )}
      </div>

      {showShareMultipleColumnsModal === item.taskId && (
        <ShareMultipleColumnsModal
          onClose={() => dispatch(hideShareMultipleColumnsModal())}
          avatarShape={taskAvatarShape}
          tasks={[item]}
          taskType={item.taskType}
        />
      )}

      {showStopSharingWithClientModal === item.taskId && (
        <StopSharingWithClientModal
          onClose={() => dispatch(hideStopSharingWithClientModal())}
          taskAvatarShape={taskAvatarShape}
          getPlaceholderImage={getPlaceholderImage}
          task={item}
        />
      )}

      {showAddSocialProfilesModal && (
        <AddSocialProfilesModal
          objectType="task"
          onClose={() => setShowAddSocialProfilesModal(false)}
          name={item.title}
          taskId={item.taskId}
          socialProfiles={item.socialProfiles}
        />
      )}

      {showUpdatePublicationLinkModal && (
        <UpdatePublicationLinkModal
          onClose={() => setShowUpdatePublicationLinkModal(false)}
          taskId={item.taskId}
        />
      )}
    </div>
  );
}
